import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Heart, Wind, Flame, Droplets, Mountain, Sparkles } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";

interface ReleasePhaseProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  eventTitle: string;
}

export const ReleasePhase: React.FC<ReleasePhaseProps> = ({ isOpen, onClose, onSave, eventTitle }) => {
  const [activeTab, setActiveTab] = useState('emotional');
  const [responses, setResponses] = useState({
    // Emotional Release
    letterToEvent: '',
    emotionsToRelease: [] as string[],
    forgivenessLetter: '',
    
    // Somatic Release
    bodyAwareness: '',
    breathworkNotes: '',
    movementRelease: '',
    
    // Cognitive Release
    challengingBeliefs: '',
    evidenceAgainst: '',
    costOfHolding: '',
    
    // Shamanic & Energetic Release
    fireCeremony: '',
    cordCuttingVisualization: '',
    elementalRelease: '',
    soundRelease: '',
    waterCeremony: '',
    earthBurial: '',
    sacredSmoke: '',
    energyReclamation: '',
    
    // Completion
    whatImReleasing: '',
    permissionStatement: '',
    releaseCommitment: '',
  });

  const emotionsList = [
    'Shame', 'Guilt', 'Anger', 'Fear', 'Grief', 'Resentment',
    'Betrayal', 'Abandonment', 'Powerlessness', 'Unworthiness'
  ];

  const handleSave = () => {
    onSave({
      release_notes: JSON.stringify(responses),
      release_completed: true
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Heart className="h-6 w-6 text-primary" />
            Release Phase: {eventTitle}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Therapeutic release techniques for emotional, somatic, and energetic clearing
          </p>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="emotional">Emotional</TabsTrigger>
            <TabsTrigger value="somatic">Somatic</TabsTrigger>
            <TabsTrigger value="shamanic">Shamanic</TabsTrigger>
            <TabsTrigger value="cognitive">Cognitive</TabsTrigger>
            <TabsTrigger value="completion">Completion</TabsTrigger>
          </TabsList>

          <TabsContent value="emotional" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Wind className="h-4 w-4 text-primary" />
                Emotional Release Work
              </h3>
              <p className="text-xs text-muted-foreground">
                Safely express and release stored emotions using expressive writing therapy
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Letter to the Event/Person</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Write an uncensored letter. Say everything you never got to say. This is for your eyes only - complete honesty.
                </p>
                <Textarea
                  value={responses.letterToEvent}
                  onChange={(e) => setResponses({ ...responses, letterToEvent: e.target.value })}
                  rows={6}
                  placeholder="Dear [Person/Situation], What you did changed me. I've carried this for so long. I need you to know that..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Emotions Ready to Release</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Name the specific emotions you're releasing (checking them helps externalize them)
                </p>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {emotionsList.map((emotion) => (
                    <div key={emotion} className="flex items-center space-x-2">
                      <Checkbox
                        id={emotion}
                        checked={responses.emotionsToRelease.includes(emotion)}
                        onCheckedChange={(checked) => {
                          setResponses({
                            ...responses,
                            emotionsToRelease: checked
                              ? [...responses.emotionsToRelease, emotion]
                              : responses.emotionsToRelease.filter(e => e !== emotion)
                          });
                        }}
                      />
                      <label htmlFor={emotion} className="text-sm cursor-pointer">
                        {emotion}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Forgiveness Letter (Optional but Powerful)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Forgiveness is for YOU, not them. It doesn't excuse their actions. It frees you from carrying their toxicity.
                </p>
                <Textarea
                  value={responses.forgivenessLetter}
                  onChange={(e) => setResponses({ ...responses, forgivenessLetter: e.target.value })}
                  rows={5}
                  placeholder="I forgive you, not because what you did was okay, but because I refuse to let this define me anymore. I release you. I release me..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="somatic" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Droplets className="h-4 w-4 text-primary" />
                Somatic Release (Body-Based)
              </h3>
              <p className="text-xs text-muted-foreground">
                Trauma is stored in the body. Release through sensation, breath, and movement.
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Body Awareness Scan</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Close your eyes. Where do you feel this memory in your body? Describe the sensation without judgment.
                </p>
                <Textarea
                  value={responses.bodyAwareness}
                  onChange={(e) => setResponses({ ...responses, bodyAwareness: e.target.value })}
                  rows={4}
                  placeholder="I feel it in my throat - tight, like I'm choking on unspoken words. My chest is heavy. There's tension in my shoulders..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Breathwork Release Protocol</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Practice: Breathe into where it hurts. On exhale, imagine releasing the energy. Document what shifted.
                </p>
                <Textarea
                  value={responses.breathworkNotes}
                  onChange={(e) => setResponses({ ...responses, breathworkNotes: e.target.value })}
                  rows={4}
                  placeholder="After 10 breaths, the tightness started to soften. I felt heat moving up and out. Tears came. The pressure lifted..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Movement as Medicine</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What movement helps you release? (shaking, dancing, hitting pillows, crying, screaming into pillow, yoga)
                </p>
                <Textarea
                  value={responses.movementRelease}
                  onChange={(e) => setResponses({ ...responses, movementRelease: e.target.value })}
                  rows={3}
                  placeholder="I'm going to do trauma release shaking for 10 minutes. Dance to angry music. Hit a pillow while screaming what I never got to say..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="shamanic" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Shamanic & Energetic Release
              </h3>
              <p className="text-xs text-muted-foreground">
                Ancient wisdom practices for energetic clearing and ceremonial release
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Fire Ceremony - Sacred Burning</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Write what you're releasing on paper. Burn it safely in a fireproof container. Watch the smoke carry it away. Fire transforms and purifies.
                </p>
                <Textarea
                  value={responses.fireCeremony}
                  onChange={(e) => setResponses({ ...responses, fireCeremony: e.target.value })}
                  rows={4}
                  placeholder="I will write: 'I release the shame from [event]. I release the belief that I am [false belief]. This no longer serves me.' Then burn it with intention, saying 'I return this to the universe, transformed by fire.'"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Cord Cutting Visualisation</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Close your eyes. Visualise the energetic cords connecting you to this event/person. See them clearly. Now, with love and firmness, cut them. Call your energy back home.
                </p>
                <Textarea
                  value={responses.cordCuttingVisualization}
                  onChange={(e) => setResponses({ ...responses, cordCuttingVisualization: e.target.value })}
                  rows={4}
                  placeholder="I saw thick dark cords from my solar plexus to [person/event]. I visualised golden scissors cutting them with each breath. I watched my energy return to me as golden light. The cords dissolved into the earth..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Elemental Release Work</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Choose your element: Earth (bury written release), Water (dissolve in bath/ocean), Air (speak it to the wind), Fire (burn it). Document your chosen ritual.
                </p>
                <Textarea
                  value={responses.elementalRelease}
                  onChange={(e) => setResponses({ ...responses, elementalRelease: e.target.value })}
                  rows={4}
                  placeholder="I chose Water. I will write what I'm releasing on water-soluble paper, go to the ocean, read it aloud to the waves, then watch it dissolve and be carried away by the tide..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Sound Release - Vocalization & Drumming</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Use your voice or drums/rattles to shake loose stuck energy. Scream, tone, chant, drum. Let sound vibration break up stagnation.
                </p>
                <Textarea
                  value={responses.soundRelease}
                  onChange={(e) => setResponses({ ...responses, soundRelease: e.target.value })}
                  rows={3}
                  placeholder="I'm going to scream into a pillow until the rage moves through. Then I'll use my drum, beating out the pain in rhythm, letting it flow out through my hands and into the earth..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Sacred Smoke - Energetic Cleansing</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Use sage, palo santo, or incense. Set clear intention: "I cleanse myself of [name what you're releasing]." Move smoke around your body with purpose.
                </p>
                <Textarea
                  value={responses.sacredSmoke}
                  onChange={(e) => setResponses({ ...responses, sacredSmoke: e.target.value })}
                  rows={3}
                  placeholder="I'll light sage and say: 'I cleanse my energy field of all residue from [event]. I release what doesn't belong to me. I reclaim my sacred space.' Moving the smoke from feet to crown..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Energy Reclamation Meditation</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Visualise all the energy you left in that event returning to you now. See it as light flowing back into your body. You're whole again.
                </p>
                <Textarea
                  value={responses.energyReclamation}
                  onChange={(e) => setResponses({ ...responses, energyReclamation: e.target.value })}
                  rows={4}
                  placeholder="I called back all the parts of myself I left behind. I saw golden threads of my essence returning from [event/person]. I felt fuller, brighter, more whole with each breath..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="cognitive" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Flame className="h-4 w-4 text-primary" />
                Cognitive Restructuring & Release
              </h3>
              <p className="text-xs text-muted-foreground">
                Challenge and burn the false beliefs formed from this event
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Challenging the Belief</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  The core belief formed may have protected you then, but is it TRUE? Is it serving you now?
                </p>
                <Textarea
                  value={responses.challengingBeliefs}
                  onChange={(e) => setResponses({ ...responses, challengingBeliefs: e.target.value })}
                  rows={4}
                  placeholder="The belief 'I'm not good enough' was formed when I was 7 years old by someone who was hurting. A child's conclusion isn't truth. It was a survival story..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Evidence Against the Belief</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  List concrete evidence that contradicts this old belief. What proves it's not true?
                </p>
                <Textarea
                  value={responses.evidenceAgainst}
                  onChange={(e) => setResponses({ ...responses, evidenceAgainst: e.target.value })}
                  rows={4}
                  placeholder="I've built a career. People choose me. I've overcome obstacles. I've been loved. I've survived. I've grown. I'm still here..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Cost of Continuing to Hold This</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What has this belief cost you? What will it cost you in the future if you don't release it?
                </p>
                <Textarea
                  value={responses.costOfHolding}
                  onChange={(e) => setResponses({ ...responses, costOfHolding: e.target.value })}
                  rows={4}
                  placeholder="It's cost me intimacy, peace, joy, opportunities. If I don't release it, I'll stay small, isolated, unfulfilled. That's too high a price..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="completion" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Mountain className="h-4 w-4 text-primary" />
                Release Completion Ritual
              </h3>
              <p className="text-xs text-muted-foreground">
                Formalize your release with intention and ceremony
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">What I Am Releasing (Be Specific)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Name exactly what you're letting go of. Make it concrete.
                </p>
                <Textarea
                  value={responses.whatImReleasing}
                  onChange={(e) => setResponses({ ...responses, whatImReleasing: e.target.value })}
                  rows={4}
                  placeholder="I release the belief that I'm not worthy of love. I release the shame that wasn't mine to carry. I release the protection that's now a prison..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Permission Statement</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Give yourself explicit permission to let this go. You needed it once. You don't anymore.
                </p>
                <Textarea
                  value={responses.permissionStatement}
                  onChange={(e) => setResponses({ ...responses, permissionStatement: e.target.value })}
                  rows={4}
                  placeholder="I give myself permission to release this story. It protected me once, but I'm safe now. I have adult resources. I don't need this anymore. I'm allowed to let go..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Symbolic Release Ritual (Optional)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  How will you ceremonially release this? (burn the letter, release into water, bury it, create art then destroy it)
                </p>
                <Textarea
                  value={responses.releaseCommitment}
                  onChange={(e) => setResponses({ ...responses, releaseCommitment: e.target.value })}
                  rows={3}
                  placeholder="I'm going to write this all out, read it aloud, then safely burn it while saying 'I release you.' I'll scatter the ashes in the ocean..."
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Release Work
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};